import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Facebook, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { navLinks, socialLinks, contactInfo } from '@/data/mock';

// Custom X (Twitter) Icon
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = () => {
  const getIcon = (iconName) => {
    const icons = { Linkedin, X: XIcon, Facebook, Youtube };
    return icons[iconName] || Linkedin;
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src={`${process.env.PUBLIC_URL}/eliteplus_logo.png`} 
                alt="ElitePlus Logo"
                className="w-12 h-12 object-contain"
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white leading-tight">
                  Elite<span className="text-cyan-400">Plus</span>
                </span>
                <span className="text-[10px] font-medium text-slate-400 tracking-wide">
                  Excellence Plus Impact
                </span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Outcome-driven IT consulting for modern enterprises. We help organizations modernize ERP, strengthen cybersecurity, and deliver transformation programs with clarity.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const Icon = getIcon(social.icon);
                return (
                  <a
                    key={social.id}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:bg-cyan-500 hover:border-cyan-500 transition-all duration-300"
                    aria-label={social.name}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  to={link.href}
                  className="text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Our Services</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/services/it-strategy" className="text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                Enterprise IT Strategy
              </Link>
              <Link to="/services/erp-advisory" className="text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                ERP / CRM Advisory
              </Link>
              <Link to="/services/cloud-modernization" className="text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                Cloud Modernization
              </Link>
              <Link to="/services/cybersecurity" className="text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                Cybersecurity & Risk
              </Link>
              <Link to="/services/digital-transformation" className="text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                Digital Transformation
              </Link>
              <a href="https://eliteplus.zohodesk.ae/portal" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm">
                EliteCare
              </a>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Contact Us</h4>
            <div className="flex flex-col gap-4">
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-3 text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm"
              >
                <Mail className="w-5 h-5 text-cyan-400" />
                {contactInfo.email}
              </a>
              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center gap-3 text-slate-400 hover:text-cyan-400 transition-colors duration-300 text-sm"
              >
                <Phone className="w-5 h-5 text-cyan-400" />
                {contactInfo.phone}
              </a>
              <div className="flex items-start gap-3 text-slate-400 text-sm">
                <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span>{contactInfo.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* SEO Location Text */}
        <div className="py-4 border-t border-slate-800">
          <p className="text-slate-600 text-xs leading-relaxed text-center">
            ElitePlus Solutions — IT Consulting &amp; Technology Services in UAE | Enterprise IT Strategy | ERP Advisory | Cloud Transformation | Cybersecurity | AI &amp; Automation | Digital Transformation | Enterprise Architecture | Managed IT Services | Serving Ras Al Khaimah, Dubai, Abu Dhabi, Sharjah, and across the United Arab Emirates
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} ElitePlus Solutions. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-300 text-sm transition-colors duration-300">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
